import { Box, Stack, Typography, alpha, styled } from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import Loading from "Components/Loading/Loading";
import IncreaseDecrease from "features/analytics/components/IncreaseDecrease";
import AnalyticsCardPanelHeader from "features/analytics/components/AnalyticsCardPanelHeader";
import useChartData from "features/analytics/hooks/useChartData";
import useFormatMetric from "features/analytics/hooks/useFormatMetric";
import { percentageTopChangeByMetric } from "features/analytics/utils/utils";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { formatPercentage, percentageOf } from "Helpers/percentage";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import { useEffect, useMemo, useState } from "react";
import { Schedule } from "@mui/icons-material";
import { getFilterableDimensionValues } from "features/analytics/services/analyticsService";
import { useCustomerRetentionCohort } from "../context/CustomerRetentionCohortProvider";

const DistributionLayout = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 220px",
  gap: theme.spacing(2),
  height: "100%",
  minHeight: 360,
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
}));

const BarTrack = styled(Box)(({ theme }) => ({
  position: "relative",
  height: 30,
  borderRadius: 8,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  overflow: "hidden",
}));

const BarFill = styled(Box)(({ theme }) => ({
  position: "absolute",
  inset: "0 auto 0 0",
  borderRadius: 8,
  background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.55)} 0%, ${theme.palette.primary.main} 100%)`,
  transition: "width 0.35s ease",
}));

const MedianCard = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  backgroundColor: alpha(theme.palette.common.white, 0.72),
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  minHeight: 0,
}));

function sortBucketLabels(labels: string[]) {
  return [...labels].sort((a, b) => {
    const aNum = Number.parseInt(a, 10);
    const bNum = Number.parseInt(b, 10);
    if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
      return aNum - bNum;
    }
    return a.localeCompare(b);
  });
}

function TimeToSecondOrderDistribution() {
  const { closedCohort, dateDimension } = useCustomerRetentionCohort();
  const { selectedProject } = useSelectedProject();
  const { chartOutput } = useFromToContext();
  const formatMetric = useFormatMetric();
  const [bucketLabels, setBucketLabels] = useState<string[]>([]);
  const [bucketLabelsLoading, setBucketLabelsLoading] = useState(true);

  useEffect(() => {
    if (!selectedProject?.id) {
      setBucketLabels([]);
      setBucketLabelsLoading(false);
      return;
    }

    setBucketLabelsLoading(true);
    getFilterableDimensionValues(
      selectedProject.id,
      ApiDimension.DIMENSION_DAYS_TO_SECOND_PURCHASE_BUCKET,
    )
      .then((labels) => setBucketLabels(sortBucketLabels(labels || [])))
      .finally(() => setBucketLabelsLoading(false));
  }, [selectedProject?.id]);

  const distributionChart = useMemo(
    () => ({
      name: "Time to Second Order Distribution",
      dimension: [ApiDimension.DIMENSION_DAYS_TO_SECOND_PURCHASE_BUCKET],
      template: ApiChartTemplate.SNAPSHOT,
      type: ApiChartType.GRAPH,
      attributes: [],
      dateDimension,

      orderBy: `${ApiDimension.DIMENSION_DAYS_TO_SECOND_PURCHASE_BUCKET}`,
      yAxisKeys: [
        {
          key: "l",
          fields: [ApiMetric.METRIC_CUSTOMERS_COUNT],
        },
      ],
    }),
    [dateDimension],
  );

  const medianChart = useMemo(
    () => ({
      name: "Median Time to Second Order",
      dimension: [ApiDimension.DIMENSION_NOT_SET],
      template: ApiChartTemplate.SNAPSHOT,
      type: ApiChartType.GRAPH,
      dateDimension,
      attributes: [],
      yAxisKeys: [
        {
          key: "l",
          fields: [ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE],
        },
      ],
    }),
    [dateDimension],
  );

  const { chartData, loading } = useChartData(
    selectedProject,
    distributionChart,
    chartOutput,
    { ignoreModel: true },
  );
  const {
    chartData: medianData,
    loading: medianLoading,
    comparisonChartData,
  } = useChartData(selectedProject, medianChart, chartOutput, {
    ignoreModel: true,
  });

  const buckets = useMemo(() => {
    const rows = chartData?.data || [];
    const counts = new Map<string, number>();

    rows.forEach((row) => {
      const label = String(row.name || "");
      if (!label) {
        return;
      }

      counts.set(label, Number(row[ApiMetric.METRIC_CUSTOMERS_COUNT] || 0));
    });

    const total = [...counts.values()].reduce((sum, count) => sum + count, 0);
    const labels =
      bucketLabels.length > 0
        ? bucketLabels
        : sortBucketLabels(
            rows.map((row) => String(row.name || "")).filter(Boolean),
          );

    return labels.map((label) => ({
      label,
      pct: total > 0 ? percentageOf(counts.get(label) || 0, total) : 0,
    }));
  }, [chartData, bucketLabels]);

  const maxPct = useMemo(
    () => Math.max(...buckets.map((bucket) => bucket.pct), 1),
    [buckets],
  );

  const medianValue = Number(
    medianData?.data?.[0]?.[ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE] ||
      0,
  );
  const medianChange = percentageTopChangeByMetric(
    medianData?.data || [],
    comparisonChartData?.data || [],
    ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE,
  );

  return (
    <GlassCard
      sx={{ height: "100%" }}
      boxProps={{
        p: 2.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AnalyticsCardPanelHeader
        icon={<Schedule fontSize="small" />}
        title="Time to Second Order (Distribution)"
        subtitle={
          closedCohort
            ? "% of customers acquired in this period who placed a second order by number of days"
            : "% of active customers in this period who placed a second order by number of days"
        }
      />

      {loading || bucketLabelsLoading ? (
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Loading />
        </Box>
      ) : (
        <DistributionLayout>
          <Stack spacing={1.75} justifyContent="center">
            {buckets.map((bucket) => (
              <Stack key={bucket.label} spacing={0.75}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" fontWeight={600}>
                    {bucket.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatPercentage(bucket.pct)}
                  </Typography>
                </Stack>
                <BarTrack>
                  <BarFill
                    sx={{
                      width: `${(bucket.pct / maxPct) * 100}%`,
                    }}
                  />
                </BarTrack>
              </Stack>
            ))}
          </Stack>

          <MedianCard>
            {medianLoading ? (
              <Loading />
            ) : (
              <Stack spacing={1} alignItems="center">
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  textAlign="center"
                >
                  Median Time to Second Order
                </Typography>
                <Typography variant="h1" fontWeight={800} color="primary.main">
                  {formatMetric(
                    ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE,
                    medianValue,
                  )}
                </Typography>
                {typeof medianChange !== "undefined" && (
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <IncreaseDecrease
                      show="icon"
                      direction={
                        medianChange > 0
                          ? "up"
                          : medianChange < 0
                            ? "down"
                            : "same"
                      }
                      value={medianChange}
                      fontSize={14}
                      useThemeColours
                    />
                    <Typography variant="caption" color="text.secondary">
                      {Math.abs(medianChange).toFixed(1)}% vs previous period
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </MedianCard>
        </DistributionLayout>
      )}
    </GlassCard>
  );
}

export default TimeToSecondOrderDistribution;
